
import { useEffect, useRef, useState } from "react";
import { Star, ChevronLeft, ChevronRight, Eye } from "lucide-react";
import { useServicesData } from "@/hooks/useServicesData";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { formatPrice } from "@/lib/currency";

export const ServicesProductsCarousel = () => {
  const { services, products, isServicesLoading, isProductsLoading } = useServicesData();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [scrollEnd, setScrollEnd] = useState(false);
  const [scrollStart, setScrollStart] = useState(true);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  
  // Combine services and products into one array
  const allItems = [
    ...(services || []).map(service => ({
      id: service.id,
      title: service.service_name,
      price: service.price,
      imageUrl: service.photo_url,
      type: 'service' as const,
      professional: service.profiles?.full_name || 'Professionnel',
      rating: 4.5, // Default rating for demonstration
      currency: service.currency || 'EUR'
    })),
    ...(products || []).map(product => ({
      id: product.id,
      title: product.title,
      price: product.price,
      imageUrl: product.photo_url,
      type: 'product' as const,
      professional: product.profiles?.full_name || 'Vendeur',
      rating: 4.2, // Default rating for demonstration
      currency: product.currency || 'EUR'
    }))
  ];

  useEffect(() => {
    const checkScrollPosition = () => {
      if (!scrollRef.current) return;
      
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setScrollStart(scrollLeft <= 10);
      setScrollEnd(scrollWidth - scrollLeft - clientWidth <= 10);
    };

    const container = scrollRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollPosition);
      // Initialize scroll position check
      checkScrollPosition();
      
      return () => {
        container.removeEventListener('scroll', checkScrollPosition);
      };
    }
  }, [allItems.length]);

  const handleItemClick = (item: typeof allItems[0], e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const route = item.type === 'service' 
      ? (isMobile ? `/service-detail-mobile/${item.id}` : `/services/${item.id}`)
      : (isMobile ? `/product-detail-mobile/${item.id}` : `/services/products/${item.id}`);
    
    console.log(`ServicesProductsCarousel - Navigating to ${route} for ${item.type} ID: ${item.id}, isMobile: ${isMobile}`);
    navigate(route);
  };

  const handleScrollLeft = () => {
    if (scrollRef.current) {
      const { scrollLeft } = scrollRef.current;
      const cardWidth = scrollRef.current.querySelector('.item-card')?.clientWidth || 0;
      const scrollAmount = Math.max(0, scrollLeft - cardWidth * 2);
      
      scrollRef.current.scrollTo({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const handleScrollRight = () => {
    if (scrollRef.current) {
      const { scrollLeft } = scrollRef.current;
      const cardWidth = scrollRef.current.querySelector('.item-card')?.clientWidth || 0;
      
      scrollRef.current.scrollTo({
        left: scrollLeft + cardWidth * 2,
        behavior: 'smooth'
      });
    }
  };

  if (isServicesLoading || isProductsLoading) {
    return (
      <div className="space-y-2">
        <h3 className="font-semibold text-base">Services et produits</h3>
        <div className="flex gap-4 overflow-hidden">
          {[1, 2].map((_, index) => (
            <div 
              key={index}
              className="min-w-[200px] rounded-lg bg-gray-200 animate-pulse h-[220px]"
            />
          ))}
        </div>
      </div>
    );
  }

  if (!allItems.length) {
    return (
      <div className="space-y-2">
        <h3 className="font-semibold text-base">Services et produits</h3>
        <div className="bg-gray-100 p-4 rounded-lg text-center text-sm text-gray-500">
          Aucun service ou produit disponible pour l'instant
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-semibold text-base">Services et produits</h3>
        <div className="flex gap-1">
          <Button 
            onClick={handleScrollLeft} 
            variant="ghost" 
            size="icon" 
            className={cn(
              "h-7 w-7 rounded-full bg-gray-100 hover:bg-gray-200 transition-opacity", 
              scrollStart ? "opacity-50 cursor-not-allowed" : "opacity-100"
            )}
            disabled={scrollStart}
            aria-label="Défiler à gauche"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <Button 
            onClick={handleScrollRight} 
            variant="ghost" 
            size="icon"
            className={cn(
              "h-7 w-7 rounded-full bg-gray-100 hover:bg-gray-200 transition-opacity",
              scrollEnd ? "opacity-50 cursor-not-allowed" : "opacity-100"
            )}
            disabled={scrollEnd}
            aria-label="Défiler à droite"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
        </div>
      </div>
      
      <div
        ref={scrollRef} 
        className="flex gap-4 pb-4 overflow-x-auto scrollbar-hide snap-x snap-mandatory scroll-smooth"
        style={{ 
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none', 
          msOverflowStyle: 'none'
        }}
      >
        {allItems.map((item, index) => (
          <div 
            key={item.id}
            className="item-card flex-shrink-0 w-[200px] rounded-lg overflow-hidden shadow-sm border hover:shadow-md transition-shadow snap-start"
          >
            <div className="aspect-[4/3] w-full bg-gray-100 relative cursor-pointer" onClick={(e) => handleItemClick(item, e)}>
              {item.imageUrl ? (
                <img 
                  src={item.imageUrl} 
                  alt={item.title} 
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-50">
                  <span className="text-gray-400 text-xs">Pas d'image</span>
                </div>
              )}
              <div className={cn(
                "absolute top-2 left-2 text-xs font-medium px-2 py-1 rounded-full text-white",
                item.type === 'service' ? "bg-blue-500" : "bg-green-500"
              )}>
                {item.type === 'service' ? 'Service' : 'Produit'}
              </div>
            </div>
            
            <div className="p-3 space-y-1">
              <h4 className="font-medium text-sm line-clamp-1">{item.title}</h4>
              <div className="flex items-center gap-1">
                <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                <span className="text-xs text-gray-600">{item.rating.toFixed(1)}</span>
              </div>
              <div className="flex justify-between items-center mt-2">
                <span className="font-semibold text-sm">{formatPrice(item.price, item.currency)}</span>
                <span className="text-xs text-gray-500 truncate max-w-[100px]">{item.professional}</span>
              </div>
              
              {/* Ajout d'un bouton "Voir" explicite */}
              <Button 
                variant="outline" 
                size="xs"
                className="w-full mt-2 text-xs border-primary text-primary hover:bg-primary/5"
                onClick={(e) => handleItemClick(item, e)}
              >
                <Eye className="mr-1 h-3 w-3" /> Voir {item.type === 'service' ? 'le service' : 'le produit'}
              </Button>
            </div>
          </div>
        ))}
      </div>

      {allItems.length > 2 && (
        <div className="flex justify-center gap-1 mt-2">
          {Array.from({ length: Math.ceil(allItems.length / 2) }).map((_, i) => (
            <div 
              key={i} 
              className={cn(
                "w-1.5 h-1.5 rounded-full transition-all", 
                Math.floor(currentIndex / 2) === i ? "bg-blue-500 w-3" : "bg-gray-300"
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
};
